import * as React from "react";
import { putSetting, type AdminSettingRow } from "@/api/admin";
import { useAction } from "@/lib/useAsync";
import { savedNotice } from "@/lib/saved";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Field, Input, Textarea } from "@/components/ui/input";
import { Notice, Warnings } from "@/components/ui/feedback";
import { BelowCostConfirm, useBelowCostQuestion } from "@/components/BelowCostConfirm";

/**
 * The operator's switches, drawn from the SERVER'S spec.
 *
 * The control for each key comes from `kind`, and a number's bounds from
 * `min` and `max`, because the worker sends them beside the value. The
 * alternative -- a table of key names and widget types written in TypeScript
 * here -- is a second copy of `worker/src/lib/config.ts`, and it is the copy
 * that stops matching the first time a bound moves.
 *
 * TWO OF THESE MOVE MONEY, and they are marked. `rail_crypto_enabled` is the
 * checkout kill-switch and `manual_approval_enabled` decides whether a paid
 * order waits for a person. Both are born off in the schema, deliberately.
 */
const MONEY_KEYS = new Set(["rail_crypto_enabled", "manual_approval_enabled"]);

/** What each key does, in the words an operator would use. */
const EXPLAIN: Record<string, string> = {
  rail_crypto_enabled:
    "When off, nobody can open a new crypto invoice. " +
    "Settling an invoice already open is unaffected.",
  manual_approval_enabled:
    "When on, a paid order waits on the Orders screen until somebody approves it.",
  crypto_discount_pct: "Taken off every order for paying in crypto.",
  coupon_field_enabled:
    "Shows the coupon box at checkout. When off, no coupon is accepted, " +
    "even one still switched on.",
  topup_discount_pct:
    "Taken off every top-up, on top of the crypto discount. " +
    "The top-up form shows the full price struck through.",
  default_package_id:
    "The plan the buy panel starts on, set from Products. " +
    "Empty: the badged plan, else the first.",
  esimgo_balance_alarm_cents:
    "The dashboard warns when the supplier balance falls below this. No email is sent.",
  pii_retention_days: "Days after delivery before an order's address and user agent are cleared.",
  support_email: "Shown on the storefront, and the reply-to on delivery mail.",
  support_telegram:
    "The Telegram account the bot's Help button opens, like @example_support. " +
    "Empty: Help shows the support email.",
  site_name: "Used in page titles and in the structured data.",
  twitter_handle: "Optional. Used in the Twitter card tags.",
  ga4_measurement_id: "Not read by the site: analytics run from the PUBLIC_GA4_ID build variable.",
  gsc_verification_token: "Optional. For Search Console's meta-tag verification.",
  org_json: "The schema.org Organization block rendered on every page.",
};

/**
 * One setting, saved on its own. On the Settings screen, and beside the
 * thing it switches where that has a screen of its own.
 */
export function SettingCard({
  setting,
  onSaved,
}: {
  setting: AdminSettingRow;
  onSaved: () => void;
}) {
  const [draft, setDraft] = React.useState<string>(String(setting.value));
  const action = useAction();
  /**
   * What the last save did. A setting the site is built from rebuilds it, and
   * that is said here -- as is a build that did not start, which arrives as a
   * warning on a save that stands.
   */
  const [saved, setSaved] = React.useState<string | null>(null);
  const { ask, question } = useBelowCostQuestion();
  const id = `setting-${setting.key}`;
  const money = MONEY_KEYS.has(setting.key);

  // Re-seeded when the server's value changes, so a reload after a save does
  // not leave the box showing what was typed rather than what actually stuck.
  React.useEffect(() => {
    setDraft(String(setting.value));
  }, [setting.value]);

  function save(value: boolean | number | string) {
    setSaved(null);
    void ask(action, (allow) => putSetting(setting.key, value, allow), (answer) => {
      // A refusal wrote nothing, so there is nothing to say and nothing to reload.
      if (!answer) return;
      // When there are warnings, they say what the save did, beneath the card.
      if (!answer.warnings?.length) setSaved(savedNotice(answer));
      onSaved();
    });
  }

  return (
    <Card className={money ? "border-warning/40" : undefined}>
      <CardHeader>
        <CardTitle className="font-mono text-xs">{setting.key}</CardTitle>
        {/* "default" rather than nothing: an operator has to be able to tell a
            value somebody chose from a value nobody has touched. */}
        {!setting.stored && <span className="text-xs text-muted-foreground">default</span>}
      </CardHeader>
      <CardContent className="space-y-3">
        {EXPLAIN[setting.key] && (
          <p className="text-xs text-muted-foreground">{EXPLAIN[setting.key]}</p>
        )}

        {setting.kind === "bool" ? (
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-medium">{setting.value ? "On" : "Off"}</span>
            <Button
              size="sm"
              variant={setting.value ? "destructive" : "default"}
              disabled={action.busy}
              onClick={() => save(!setting.value)}
            >
              {setting.value ? "Turn off" : "Turn on"}
            </Button>
          </div>
        ) : setting.kind === "json" ? (
          <>
            <Field label="Value" htmlFor={id}>
              <Textarea
                id={id}
                rows={5}
                className="font-mono text-xs"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
              />
            </Field>
            <Button
              size="sm"
              disabled={action.busy || draft === String(setting.value)}
              onClick={() => save(draft)}
            >
              Save
            </Button>
          </>
        ) : (
          <>
            <Field
              label="Value"
              htmlFor={id}
              hint={
                setting.kind === "int" && setting.min !== undefined && setting.max !== undefined
                  ? `${setting.min} to ${setting.max}`
                  : undefined
              }
            >
              <Input
                id={id}
                size="sm"
                type={setting.kind === "int" ? "number" : "text"}
                min={setting.min}
                max={setting.max}
                maxLength={setting.maxLength}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
              />
            </Field>
            <Button
              size="sm"
              disabled={action.busy || draft === String(setting.value)}
              onClick={() => save(setting.kind === "int" ? Number(draft) : draft)}
            >
              Save
            </Button>
          </>
        )}

        {action.error && <Notice tone="error">{action.error}</Notice>}
        {saved && <Notice tone="success">{saved}</Notice>}
        <BelowCostConfirm {...question} busy={action.busy} />
        <Warnings warnings={action.warnings} />
      </CardContent>
    </Card>
  );
}
